import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ActivityMetrics, ChurnRiskData, ChurnRiskHistoryRow, RecommendedActionItemV2 } from '@/models/ChurnRisk';

function parseActivityMetrics(parsed: Record<string, unknown>): ActivityMetrics {
  const raw = parsed.activityMetrics;
  const empty: ActivityMetrics = {
    lastActivityDate: '',
    daysSinceLastActivity: -1,
    totalActivities: 0,
    activitiesLast7d: 0,
    activitiesLast30d: 0,
    avgActivitiesPerDay: 0,
    activityStreak: 0,
    maxActivityStreak: 0,
  };
  if (typeof raw !== 'object' || raw === null) return empty;

  const o = raw as Record<string, unknown>;
  const lastRaw = o.lastActivityDate;
  const lastActivityDate =
    lastRaw != null && String(lastRaw).trim() !== '' && String(lastRaw).toLowerCase() !== 'null' ? String(lastRaw) : '';

  const daysSinceLastActivity =
    typeof o.daysSinceLastActivity === 'number' && !Number.isNaN(o.daysSinceLastActivity)
      ? o.daysSinceLastActivity
      : lastActivityDate
      ? 0
      : -1;

  const num = (k: string) => {
    const v = o[k];
    return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
  };

  return {
    lastActivityDate,
    daysSinceLastActivity,
    totalActivities: num('totalActivities'),
    activitiesLast7d: num('activitiesLast7d'),
    activitiesLast30d: num('activitiesLast30d'),
    avgActivitiesPerDay: num('avgActivitiesPerDay'),
    activityStreak: num('activityStreak'),
    maxActivityStreak: num('maxActivityStreak'),
  };
}

function coalescePhone(raw: Record<string, unknown>): string {
  const p = raw.churnRiskData;
  const obj = typeof p === 'object' && p !== null ? (p as Record<string, unknown>) : {};
  const fromChurn = obj.phoneNumber ?? obj.phone;
  if (typeof fromChurn === 'string' && fromChurn.trim()) return fromChurn.trim();
  const meta = raw.stripeMetadata;
  if (typeof meta === 'object' && meta !== null) {
    const m = meta as Record<string, unknown>;
    const ph = m.phone ?? m.phone_number;
    if (typeof ph === 'string' && ph.trim()) return ph.trim();
  }
  return '';
}

function formatPlanLabel(raw: Record<string, unknown>): string {
  const active = Number(raw.stripeActiveSubscriptions ?? 0) > 0;
  if (!active) return 'Free';

  const stripeData = raw.stripeData;
  if (typeof stripeData !== 'object' || stripeData === null) return 'Subscriber';

  const subs = (stripeData as Record<string, unknown>).subscriptions;
  if (!Array.isArray(subs) || subs.length === 0) return 'Subscriber';

  const sub = subs[0] as Record<string, unknown>;
  const items = sub.items;
  const item = Array.isArray(items) && items.length > 0 ? (items[0] as Record<string, unknown>) : null;
  if (!item) return 'Subscriber';

  const ua = item.unit_amount ?? item.unitAmount;
  const interval = (item.interval as string) || 'month';
  if (typeof ua === 'number') {
    const dollars = (ua / 100).toFixed(0);
    const iv = interval === 'year' ? 'yr' : 'mo';
    return `${interval === 'year' ? 'Annual' : 'Monthly'} · $${dollars}/${iv}`;
  }
  return 'Subscriber';
}

function parseRecommendedActionsV2(parsed: Record<string, unknown>): RecommendedActionItemV2[] {
  const raw = parsed.recommendedActionsV2;
  if (!Array.isArray(raw)) return [];
  const out: RecommendedActionItemV2[] = [];
  for (const el of raw) {
    if (typeof el !== 'object' || el === null) continue;
    const r = el as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id.trim() : '';
    const label = typeof r.label === 'string' ? r.label.trim() : '';
    if (!id || !label) continue;
    const reason = typeof r.reason === 'string' && r.reason.trim() ? r.reason.trim() : undefined;
    const source = typeof r.source === 'string' && r.source.trim() ? r.source.trim() : undefined;
    if (reason && source) out.push({ id, label, reason, source });
    else if (reason) out.push({ id, label, reason });
    else if (source) out.push({ id, label, source });
    else out.push({ id, label });
  }
  return out;
}

function parseChurnDict(raw: Record<string, unknown>): Record<string, unknown> {
  const c = raw.churnRiskData;
  if (c == null) return {};
  if (typeof c === 'string') {
    try {
      return JSON.parse(c) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  if (typeof c === 'object') return c as Record<string, unknown>;
  return {};
}

function numOrUndef(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  return undefined;
}

/** Strips `#n` collision suffix from API history map keys (ISO instant is the prefix). */
function historyKeyToIsoInstant(key: string): string {
  const i = key.indexOf('#');
  return i >= 0 ? key.slice(0, i) : key;
}

/** New storage: append-only array from API (`churnRiskSnapshots`). */
function parseChurnRiskSnapshotsArray(arr: unknown[]): ChurnRiskHistoryRow[] {
  const out: ChurnRiskHistoryRow[] = [];
  for (const el of arr) {
    if (typeof el !== 'object' || el === null) continue;
    const s = el as Record<string, unknown>;
    const calculatedAtRaw = s.calculatedAt;
    const calculatedAt =
      typeof calculatedAtRaw === 'string' && calculatedAtRaw.trim() !== '' ? calculatedAtRaw.trim() : '';
    if (!calculatedAt) continue;
    const riskScore = Number(s.riskScore ?? 0);
    const riskTier = String(s.riskTier ?? '').trim();
    const engagementLevel = String(s.engagementLevel ?? '').trim();
    const subscriptionStatus = String(s.subscriptionStatus ?? '').trim();
    let eventTypeContribution: number | undefined;
    const etr = s.eventTypeRisk;
    if (typeof etr === 'object' && etr !== null) {
      eventTypeContribution = numOrUndef((etr as Record<string, unknown>).contribution);
    }
    out.push({
      calculatedAt: historyKeyToIsoInstant(calculatedAt),
      riskScore: Number.isFinite(riskScore) ? riskScore : 0,
      riskTier,
      engagementLevel,
      subscriptionStatus,
      eventTypeContribution,
    });
  }
  out.sort((a, b) => new Date(a.calculatedAt).getTime() - new Date(b.calculatedAt).getTime());
  return out;
}

/** Legacy: `churnRiskHistory` map (timestamp key → snapshot). */
function parseChurnRiskHistoryMap(parsed: Record<string, unknown>): ChurnRiskHistoryRow[] {
  const raw = parsed.churnRiskHistory;
  if (typeof raw !== 'object' || raw === null) return [];
  const out: ChurnRiskHistoryRow[] = [];
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v !== 'object' || v === null) continue;
    const s = v as Record<string, unknown>;
    const riskScore = Number(s.riskScore ?? 0);
    const riskTier = String(s.riskTier ?? '').trim();
    const engagementLevel = String(s.engagementLevel ?? '').trim();
    const subscriptionStatus = String(s.subscriptionStatus ?? '').trim();
    let eventTypeContribution: number | undefined;
    const etr = s.eventTypeRisk;
    if (typeof etr === 'object' && etr !== null) {
      eventTypeContribution = numOrUndef((etr as Record<string, unknown>).contribution);
    }
    out.push({
      calculatedAt: historyKeyToIsoInstant(k),
      riskScore: Number.isFinite(riskScore) ? riskScore : 0,
      riskTier,
      engagementLevel,
      subscriptionStatus,
      eventTypeContribution,
    });
  }
  out.sort((a, b) => new Date(a.calculatedAt).getTime() - new Date(b.calculatedAt).getTime());
  return out;
}

/** Prefers `churnRiskSnapshots` array; falls back to legacy `churnRiskHistory` map. */
function parseChurnRiskHistory(parsed: Record<string, unknown>): ChurnRiskHistoryRow[] {
  const arr = parsed.churnRiskSnapshots;
  if (Array.isArray(arr) && arr.length > 0) {
    return parseChurnRiskSnapshotsArray(arr);
  }
  return parseChurnRiskHistoryMap(parsed);
}

function defaultChurnRiskData(raw: Record<string, unknown>, parsed: Record<string, unknown>): ChurnRiskData {
  const riskScore = Number(parsed.riskScore ?? 0);
  const rawMl = Number(parsed.mlChurnProbability);
  const mlChurnProbability =
    Number.isFinite(rawMl) && rawMl >= 0 && rawMl <= 1 ? rawMl : null;
  const churnProbabilityForUi = mlChurnProbability ?? Math.min(1, Math.max(0, riskScore / 100));
  const engagementRaw = String(parsed.engagementLevel ?? 'medium').toLowerCase();
  const engagementLevel =
    engagementRaw === 'high' || engagementRaw === 'low' || engagementRaw === 'inactive' || engagementRaw === 'medium'
      ? (engagementRaw as ChurnRiskData['engagementLevel'])
      : 'medium';

  let subscriptionStatus: ChurnRiskData['subscriptionStatus'] = 'cancelled';
  const subRaw = String(parsed.subscriptionStatus ?? '').toLowerCase();
  if (subRaw === 'active' || subRaw === 'trialing') subscriptionStatus = subRaw;
  else if (subRaw === 'free') subscriptionStatus = 'expired';
  else if (Number(raw.stripeActiveSubscriptions ?? 0) > 0) subscriptionStatus = 'active';

  const createdAt = raw.createdAt as string | undefined;
  const activityMetrics = parseActivityMetrics(parsed);

  const ltv = typeof raw.stripeTotalLifetimeValue === 'number' ? raw.stripeTotalLifetimeValue : 0;

  const now = new Date().toISOString();
  const parsedRecommendedActions = Array.isArray(parsed.recommendedActions)
    ? parsed.recommendedActions.filter(
        (action): action is string => typeof action === 'string' && action.trim().length > 0,
      )
    : [];
  const recommendedActionsV2 = parseRecommendedActionsV2(parsed);
  const recommendedActionsLlmEnrichedAt =
    typeof parsed.recommendedActionsLlmEnrichedAt === 'string' && parsed.recommendedActionsLlmEnrichedAt.trim()
      ? parsed.recommendedActionsLlmEnrichedAt.trim()
      : undefined;
  const labelsFromV2 = recommendedActionsV2.map((a) => a.label);
  const fallbackRecommendedActions =
    riskScore >= 75
      ? [
          'Schedule a personal retention call within 24 hours',
          'Offer a temporary discount or plan adjustment',
          'Escalate account review to customer success lead',
        ]
      : riskScore >= 60
      ? [
          'Send a proactive check-in email with support options',
          'Offer onboarding refresh or product training session',
          'Create a follow-up task for account owner this week',
        ]
      : riskScore >= 30
      ? ['Share helpful product tips based on recent activity', 'Trigger a light-touch re-engagement campaign']
      : [];
  const recommendedActions =
    labelsFromV2.length > 0
      ? labelsFromV2
      : parsedRecommendedActions.length > 0
        ? parsedRecommendedActions
        : fallbackRecommendedActions;

  return {
    ...(mlChurnProbability !== null ? { mlChurnProbability } : {}),
    ...(recommendedActionsLlmEnrichedAt ? { recommendedActionsLlmEnrichedAt } : {}),
    riskScore: Number.isFinite(riskScore) ? riskScore : 0,
    riskFactors: [],
    engagementLevel,
    recommendedActions,
    recommendedActionsV2: recommendedActionsV2.length > 0 ? recommendedActionsV2 : undefined,
    lifetimeValue: ltv,
    subscriptionStatus,
    returnRate: 0,
    activityMetrics,
    usagePatterns: {
      sessionCount: 0,
      avgSessionDuration: 0,
      pagesVisited: [],
      componentDiversity: 0,
      weekdayUsage: 0,
      usageConsistency: 0,
    },
    churnPrediction: {
      churnProbability: churnProbabilityForUi,
      churnRiskLevel:
        churnProbabilityForUi >= 0.6 ? 'high' : churnProbabilityForUi >= 0.3 ? 'medium' : 'low',
      confidenceScore: 0.5,
      lastModelUpdate: now,
    },
    onboardingCompleted: true,
    firstActivityDate: (typeof parsed.firstActivityDate === 'string' && parsed.firstActivityDate) || createdAt || now,
    featureAdoptionRate: 0,
    lastUpdated: now,
    dataVersion: '1',
  };
}

/** Map StripeData.subscriptions (JSONB) into the shape SubscriptionsSection expects. */
function subscriptionsFromStripeData(raw: Record<string, unknown>): unknown[] {
  const stripeData = raw.stripeData;
  if (typeof stripeData !== 'object' || stripeData === null) return [];

  const subs = (stripeData as Record<string, unknown>).subscriptions;
  if (!Array.isArray(subs) || subs.length === 0) return [];

  return subs.map((sub, idx) => {
    const s = sub as Record<string, unknown>;
    const items = s.items;
    const list = Array.isArray(items) ? items : [];
    const stripeCreatedAt = typeof s.stripeCreatedAt === 'number' ? s.stripeCreatedAt : undefined;
    const currentPeriodEnd = typeof s.current_period_end === 'number' ? s.current_period_end : stripeCreatedAt ?? 0;

    return {
      id: `sub_${idx}`,
      stripeSubscriptionId: `sub_demo_${idx}`,
      status: Number(raw.stripeActiveSubscriptions ?? 0) > 0 ? 'active' : 'canceled',
      currency: (raw.stripeCurrency as string) || 'usd',
      cancel_at_period_end: false,
      collection_method: 'charge_automatically',
      stripeCreatedAt,
      items: {
        data: list.map((it, j) => {
          const el = it as Record<string, unknown>;
          const ua = typeof el.unit_amount === 'number' ? el.unit_amount : 0;
          const qty = typeof el.quantity === 'number' ? el.quantity : 1;
          const interval = (el.interval as string) || 'month';
          const ic = typeof el.interval_count === 'number' ? el.interval_count : 1;
          return {
            id: `si_${idx}_${j}`,
            quantity: qty,
            price: {
              id: `price_${idx}_${j}`,
              unit_amount: ua,
              currency: (raw.stripeCurrency as string) || 'usd',
              recurring: { interval, interval_count: ic },
            },
            plan: {
              id: `plan_${idx}_${j}`,
              amount: ua,
              currency: (raw.stripeCurrency as string) || 'usd',
              interval,
              interval_count: ic,
            },
            current_period_start: stripeCreatedAt,
            current_period_end: currentPeriodEnd,
          };
        }),
      },
    };
  });
}

/**
 * Maps .NET `AudienceMember` JSON (camelCase) to the legacy `MemberOverview` shape used by MemberProfile.
 */
export function normalizeAudienceMemberDetail(raw: unknown): MemberOverview {
  const m = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  const id = String(m.id ?? '');
  const name = String(m.name ?? '').trim() || 'Member';
  const email = String(m.email ?? '').trim();
  const createdAt =
    typeof m.createdAt === 'string'
      ? m.createdAt
      : m.createdAt != null
      ? new Date(m.createdAt as string | number).toISOString()
      : new Date().toISOString();

  const parsed = parseChurnDict(m);
  const churnRiskHistory = parseChurnRiskHistory(parsed);
  const parsedChurnRiskData = defaultChurnRiskData(m, parsed);
  const act = parsedChurnRiskData.activityMetrics;
  const lastActivityFromMetrics =
    act.daysSinceLastActivity >= 0 && act.lastActivityDate.trim() !== '' ? act.lastActivityDate : undefined;

  const phoneNumber = coalescePhone(m) || undefined;
  const subscriptionPlan = formatPlanLabel(m);

  const overview: MemberOverview = {
    id,
    name,
    email,
    phoneNumber,
    createdByName: String(m.createdByName ?? m.createdBy ?? '—'),
    createdAt,
    attributes: [],
    membership: { segments: { in: [], notIn: [] }, campaigns: [] },
    recommendedActions: parsedChurnRiskData.recommendedActions,
    churnRiskData: parsedChurnRiskData,
    parsedChurnRiskData,
    activityLogs: [],
    lastActivity: lastActivityFromMetrics,
    engagement: parsedChurnRiskData.engagementLevel,
    status: parsedChurnRiskData.subscriptionStatus,
    totalActivities: act.totalActivities,
    subscriptionPlan,
    stripeTotalLifetimeValue: typeof m.stripeTotalLifetimeValue === 'number' ? m.stripeTotalLifetimeValue : undefined,
    churnRiskHistory: churnRiskHistory.length > 0 ? churnRiskHistory : undefined,
  };

  (overview as unknown as Record<string, unknown>).subscriptions = subscriptionsFromStripeData(m);
  (overview as unknown as Record<string, unknown>).invoices = [];

  return overview;
}
